import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { logger } from '../utils/logger';
import type { AnalyzedRepository, PaginationInfo } from '../types/history';

interface RawRepository {
  repositoryId: string;
  repositoryName: string;
  repositoryUrl?: string;
  repositoryType?: string;
  status?: string;
  hasAnalysis?: boolean;
  analysisScore?: number | null;
  maintenanceScore?: number | null;
  lastAccessedAt?: string;
}

function mapStatus(
  status: string,
  hasAnalysis: boolean,
  analysisScore?: number | null,
): {
  executionStatus: AnalyzedRepository['executionStatus'];
  health: AnalyzedRepository['health'];
} {
  const apiStatus = status?.toLowerCase();

  let executionStatus: AnalyzedRepository['executionStatus'] = 'completed';
  if (apiStatus === 'analyzing' || apiStatus === 'processing') {
    executionStatus = 'analyzing';
  } else if (apiStatus === 'error' || apiStatus === 'failed') {
    executionStatus = 'failed';
  }

  let health: AnalyzedRepository['health'] = null;
  if (
    executionStatus === 'completed' &&
    hasAnalysis &&
    analysisScore !== null &&
    analysisScore !== undefined
  ) {
    health = analysisScore < 60 ? 'issues' : 'healthy';
  }

  return { executionStatus, health };
}

export function useAnalysisHistory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const api = useAuthenticatedApi();
  const [loading, setLoading] = useState(true);
  const [repositories, setRepositories] = useState<AnalyzedRepository[]>([]);

  // Read filter/sort/page state from URL params
  const searchQuery = searchParams.get('q') || '';
  const filterExecutionStatus = searchParams.get('status') || 'all';
  const sortBy = searchParams.get('sort') || 'lastAnalysis';
  const sortOrder = (searchParams.get('order') as 'asc' | 'desc') || 'desc';
  const filterDateRange = searchParams.get('date') || 'all';
  const urlPage = Number(searchParams.get('page')) || 1;

  const [pagination, setPagination] = useState<PaginationInfo>({
    page: urlPage,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [forceReload, setForceReload] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  // URL-backed setters
  const setSearchQuery = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('q', value);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
  };
  const setFilterExecutionStatus = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'all') next.delete('status');
      else next.set('status', value);
      next.delete('page');
      return next;
    });
  };
  const setSortBy = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'lastAnalysis') next.delete('sort');
      else next.set('sort', value);
      next.delete('page');
      return next;
    });
  };
  const setSortOrder = (value: 'asc' | 'desc' | ((prev: 'asc' | 'desc') => 'asc' | 'desc')) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const resolved = typeof value === 'function' ? value(sortOrder) : value;
      if (resolved === 'desc') next.delete('order');
      else next.set('order', resolved);
      return next;
    });
  };
  const setFilterDateRange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'all') next.delete('date');
      else next.set('date', value);
      next.delete('page');
      return next;
    });
  };

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Trigger data reload when filter/sort criteria change
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setForceReload((n) => n + 1);
  }, [debouncedSearch, filterExecutionStatus, sortBy, sortOrder, filterDateRange]);

  const loadAnalysisHistory = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        const response = await api.get('/analysis/repositories', {
          params: {
            page,
            perPage: pagination.limit,
            ...(debouncedSearch && { search: debouncedSearch }),
            sortBy,
            sortOrder,
          },
        });

        const data = response.data;
        const repos = data.data || [];
        const count = data.meta?.total ?? 0;

        const transformedRepos: AnalyzedRepository[] = repos.map((repo: RawRepository) => {
          const provider = (repo.repositoryType || 'github').toLowerCase();
          const isUpload = provider === 'upload' || provider === 'sample';

          let fullName = repo.repositoryName;

          if (!isUpload && repo.repositoryUrl) {
            try {
              const url = new URL(repo.repositoryUrl);
              const pathParts = url.pathname.split('/').filter(Boolean);
              if (pathParts.length >= 2) {
                fullName = `${pathParts[0]}/${pathParts[1]}`;
              }
            } catch {
              logger.error('Failed to parse repository URL', { repositoryUrl: repo.repositoryUrl });
            }
          }

          const { executionStatus, health } = mapStatus(
            repo.status ?? '',
            repo.hasAnalysis ?? false,
            repo.analysisScore,
          );

          return {
            id: repo.repositoryId,
            name: repo.repositoryName,
            fullName: fullName,
            provider: provider as 'github' | 'gitlab' | 'bitbucket' | 'upload',
            source: isUpload ? 'upload' : 'git',
            executionStatus,
            health,
            lastAnalysis: repo.lastAccessedAt ? new Date(repo.lastAccessedAt) : null,
            analysisCount: repo.hasAnalysis ? 1 : 0,
            devScore: repo.analysisScore ?? null,
            maintenanceScore: repo.maintenanceScore ?? null,
            url: repo.repositoryUrl || '',
          };
        });

        setRepositories(transformedRepos);

        const totalCount = data.totalCount ?? count;
        const totalPages = Math.ceil(totalCount / pagination.limit);
        setPagination({
          page,
          limit: pagination.limit,
          total: totalCount,
          totalPages: totalPages,
        });
      } catch (error) {
        logger.error('Failed to load analysis history', { error });
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, debouncedSearch, sortBy, sortOrder, api],
  );

  useEffect(() => {
    loadAnalysisHistory(urlPage);
  }, [urlPage, forceReload, loadAnalysisHistory]);

  // Client-side filters for execution status and date range
  const filteredRepositories = useMemo(
    () =>
      repositories.filter((repo) => {
        const matchesExecution =
          filterExecutionStatus === 'all' || repo.executionStatus === filterExecutionStatus;

        let matchesDateRange = true;
        if (filterDateRange !== 'all' && repo.lastAnalysis) {
          const days = parseInt(filterDateRange, 10);
          const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
          matchesDateRange = new Date(repo.lastAnalysis) >= cutoff;
        } else if (filterDateRange !== 'all' && !repo.lastAnalysis) {
          matchesDateRange = false;
        }

        return matchesExecution && matchesDateRange;
      }),
    [repositories, filterExecutionStatus, filterDateRange],
  );

  const totalPages = pagination.totalPages;

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newPage <= 1) next.delete('page');
      else next.set('page', String(newPage));
      return next;
    });
  };

  return {
    loading,
    repositories,
    filteredRepositories,
    pagination,
    totalPages,
    searchQuery,
    filterExecutionStatus,
    sortBy,
    sortOrder,
    filterDateRange,
    debouncedSearch,
    setSearchQuery,
    setFilterExecutionStatus,
    setSortBy,
    setSortOrder,
    setFilterDateRange,
    handlePageChange,
  };
}
